import React from 'react';
import { compose } from '@xo-union/react-css-modules';
import { Field as FormikField, FieldProps, Form as FormikForm } from 'formik';
import { Field as FormField, WysiwygField } from '../../../libs/gs-component-form';
import { FormBlockColumn } from '@xo-union/tk-component-form-grid';
import { Select, Option } from '@xo-union/tk-component-fields';
import { Checkbox } from '@xo-union/tk-component-switches';
import { Footer } from '../../FormComponents';
import styles from './Form.module.scss';
import { DeleteOrDiscard } from '../DeleteOrDiscard';
import { EDITOR_MODULES, EDITOR_FORMATS } from './constants';
import { DressCodeFormValues } from './types';
import { Content } from '../../../libs/gs-component-right-rail';
import AffiliateCard from './AffiliateCard';
import { AttireCategoryOption } from '../../../hooks/useAttireCategories';
import { useAiTextExperimentContext } from '../../../context/AiTextExperimentContext';

interface FormProps {
  values: DressCodeFormValues;
  setFieldValue: (field: keyof DressCodeFormValues, value: DressCodeFormValues[keyof DressCodeFormValues]) => void;
  submitForm: () => void;
  loading: boolean;
  onClose: () => void;
  handleDressCodeTypeChange: (
    value: string | React.ChangeEvent<HTMLSelectElement>,
    values: DressCodeFormValues,
    form: any // Formik helpers
  ) => void;
  discardOrDelete: () => void;
  isNew: boolean;
  attireCategories: AttireCategoryOption[];
  showArticlesLabel?: string;
  selectPlaceholder?: string;
  selectLabel?: string;
  titleLabel?: string;
  descriptionPlaceholder?: string;
  deleteLabel?: string;
}

const Form: React.FC<FormProps> = ({
  values,
  setFieldValue,
  submitForm,
  loading,
  onClose,
  handleDressCodeTypeChange,
  discardOrDelete,
  isNew,
  attireCategories,
  showArticlesLabel = 'Show articles from The Knot about your dress code',
  selectPlaceholder = 'Select a Dress Code',
  selectLabel = 'Dress Code',
  titleLabel = 'Title',
  descriptionPlaceholder = 'Customize your dress code description to give guests a better idea of what to wear to your wedding.',
  deleteLabel = 'Delete dress code item',
}) => {
  const [selectOpen, setSelectOpen] = React.useState<boolean>(false);
  const hasSelection = values.attireCategoryId != null && values.attireCategoryId !== '';
  const computedSelectLabel = selectOpen ? '' : hasSelection ? selectLabel : selectPlaceholder;
  const { showAiButton } = useAiTextExperimentContext();
  return (
    <>
      <Content className={styles.dressCodeItemContent} loading={loading}>
        <FormikForm>
          <FormBlockColumn>
            <FormikField name="attireCategoryId">
              {({ field, meta, form }: FieldProps) => (
                <Select
                  {...field}
                  aria-label="DressCode Type"
                  data-testid="dress-code-type-select"
                  onFocus={() => setSelectOpen(true)}
                  onBlur={() => {
                    // defer close to let Select finish internal close
                    setTimeout(() => setSelectOpen(false), 50);
                  }}
                  onChange={(val: any) => {
                    const normalized = typeof val === 'string' ? val : val?.option?.value ?? val?.target?.value ?? '';
                    // defer close so Select can finish internal behavior
                    setTimeout(() => setSelectOpen(false), 50);
                    handleDressCodeTypeChange(normalized, values, form);
                  }}
                  label={computedSelectLabel}
                  classes={compose({
                    'is-closed': styles.isClosed,
                    'is-open': styles.isOpen,
                    'is-neutral': styles.isNeutral,
                  })}
                  state={meta.touched && meta.error ? 'invalid' : 'neutral'}
                  subText={meta.touched && meta.error ? meta.error : undefined}
                >
                  {attireCategories.map(option => (
                    <Option key={option.value} value={option.value}>
                      <span className={styles.optionWrapper}>
                        <span>{option.label}</span>
                        {option.description && <span className={styles.optionDescription}>{option.description}</span>}
                      </span>
                    </Option>
                  ))}
                </Select>
              )}
            </FormikField>
          </FormBlockColumn>

          <FormBlockColumn>
            <FormikField
              component={FormField}
              name="title"
              label={titleLabel}
              inputProps={{ 'data-testid': 'dress-code-title-input' }}
              showAiButton={showAiButton}
            />
          </FormBlockColumn>

          <FormBlockColumn>
            <FormikField
              name="description"
              modules={EDITOR_MODULES}
              formats={EDITOR_FORMATS}
              placeholder={descriptionPlaceholder}
              component={WysiwygField}
              bounds=".paragraph-item"
              inputProps={{ 'data-testid': 'dress-code-description-input' }}
              showAiButton={showAiButton}
            />
            {values.attireCategoryId && values.title && (
              <div className={styles.additionalOptions}>
                <Checkbox
                  checked={values.showArticles}
                  onChange={() => setFieldValue('showArticles', !values.showArticles)}
                  data-testid="dress-code-articles-checkbox"
                >
                  {showArticlesLabel}
                </Checkbox>
                <AffiliateCard
                  checked={values.showAffiliates}
                  onChange={() => setFieldValue('showAffiliates', !values.showAffiliates)}
                  dataTestId="dress-code-affiliates-checkbox"
                />
              </div>
            )}
          </FormBlockColumn>

          <FormBlockColumn>
            <DeleteOrDiscard onClick={discardOrDelete} isNew={isNew}>
              {deleteLabel}
            </DeleteOrDiscard>
          </FormBlockColumn>
        </FormikForm>
      </Content>

      <Footer primaryOnClick={submitForm} showSpinner={loading} useSaveAndPreview={true} secondaryOnClick={onClose} />
    </>
  );
};

export default Form;
